import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, Clock, Briefcase } from 'lucide-react';
import { openRoles } from './openRoles';

function RoleBulletList({ items, accentClass }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item) => (
        <li key={item} className="text-sm text-muted-foreground flex items-start gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${accentClass} mt-1.5 flex-shrink-0`} />
          {item}
        </li>
      ))}
    </ul>
  );
}

function OpenRoleAccordionItem({ role, onApplyClick }) {
  const slug = role.title.toLowerCase().replace(/\s+/g, '-');
  return (
    <AccordionItem
      value={`role-${slug}`}
      className="bg-card border border-border rounded-xl px-6 overflow-hidden"
      data-testid={`careers-role-${slug}`}
    >
      <AccordionTrigger className="hover:no-underline py-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-left">
          <span className="font-display font-semibold text-base">{role.title}</span>
          <div className="flex gap-2">
            <Badge variant="secondary" className="font-mono text-[10px] tracking-wider">
              <MapPin className="w-3 h-3 mr-1" /> {role.location}
            </Badge>
            <Badge variant="secondary" className="font-mono text-[10px] tracking-wider">
              <Clock className="w-3 h-3 mr-1" /> {role.type}
            </Badge>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-5">
        <p className="text-sm text-muted-foreground mb-4">{role.summary}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <p className="font-mono text-xs tracking-wider uppercase text-muted-foreground mb-2">Responsibilities</p>
            <RoleBulletList items={role.responsibilities} accentClass="bg-primary/50" />
          </div>
          <div>
            <p className="font-mono text-xs tracking-wider uppercase text-muted-foreground mb-2">Requirements</p>
            <RoleBulletList items={role.requirements} accentClass="bg-[#3E6F86]/50" />
          </div>
        </div>
        <Button
          className="mt-4 gap-2"
          onClick={() => onApplyClick(role.title)}
          data-testid={`careers-role-apply-${slug}`}
        >
          <Briefcase className="w-4 h-4" />
          Apply for this Role
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function CareersOpenRoles({ onApplyClick }) {
  return (
    <section
      className="py-14 sm:py-18 bg-[hsl(var(--bone-2))]"
      data-testid="careers-open-roles"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-3">Open Positions</p>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-8">
          Current Openings
        </h2>
        <Accordion type="single" collapsible className="space-y-3">
          {openRoles.map((role) => (
            <OpenRoleAccordionItem
              key={role.title}
              role={role}
              onApplyClick={onApplyClick}
            />
          ))}
        </Accordion>
      </div>
    </section>
  );
}
